import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvLink,
	EvSection,
	EvSpinner,
	EvSwitch,
	EvTitle,
	IFilterValues,
	TABLE_FILTERS_SR_SUMMARY
} from '@evinced-private/ui-common';

import {
	MOBILE_SDK_DASHBOARD_PAGE_FILTERS_SECTION_ID,
	MOBILE_SDK_DASHBOARD_PAGE_MAIN_TITLE_ID,
	MOBILE_SDK_DASHBOARD_PAGE_OLDER_TESTS_LINK_ID,
	MOBILE_SDK_SHOW_ONLY_LATEST_TESTS_TOGGLE_ID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import omit from 'lodash-es/omit';
import { getAllTestsTooltipText } from 'src/apps/mobile-sdk/helpers/MobileSdkDashboardHelpers';
import { MobileSdkTestsPageFilterDefinitions } from 'src/apps/mobile-sdk/helpers/MobileSdkTestsPageFilterDefinitionsHelper';
import { useMobileSdkFilterOptionsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkFilterOptionsQuery';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { MOBILE_SDK_TESTS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { showRecentTestKey } from 'src/common/providers/dashboard/TablesCustomStateHelper';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import {
	PLATFORM_OPTIONS,
	SEVERITY_OPTIONS,
	TESTS_PERIOD_OPTIONS
} from './MobileSdkTestsFilterDefinitions';

import './MobileSdkTestsPageHeader.scss';

const MOBILE_SDK_PAGE_TITLE = 'All tests';

interface MobileSdkTestsPageHeaderProps {
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	showRecentTest: boolean;
}

export const MobileSdkTestsPageHeader: FC<MobileSdkTestsPageHeaderProps> = ({
	filters,
	onFilterChange,
	showRecentTest
}) => {
	const { onTableParamStatusChange } = useTableState();
	const filterOptionsQuery = useMobileSdkFilterOptionsQuery();

	const initialValues = useMemo(() => {
		const selectFilters = omit(filters, 'searchValue');

		return {
			text: { searchValue: filters?.searchValue },
			select: selectFilters
		};
	}, [filters]);

	const filterOptions = useMemo(() => {
		const staticFilterOptions = {
			platform: PLATFORM_OPTIONS,
			sessionsPeriod: TESTS_PERIOD_OPTIONS,
			severity: SEVERITY_OPTIONS
		};

		const dynamicFilterOptions = {
			sdkType: filterOptionsQuery.data?.sdkType ?? [],
			sdkVersion: filterOptionsQuery.data?.sdkVersion ?? [],
			appName: filterOptionsQuery.data?.appName ?? [],
			appVersion: filterOptionsQuery.data?.appVersion ?? []
		};

		return { ...staticFilterOptions, ...dynamicFilterOptions };
	}, [filterOptionsQuery.data]);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(MOBILE_SDK_TESTS_TABLE_ID, flatFilters);
	};

	const { isLoading } = filterOptionsQuery;

	return (
		<div className="mobile-sdk-tests-header">
			<div className="main-title-container">
				<EvTitle
					titleText={MOBILE_SDK_PAGE_TITLE}
					dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_MAIN_TITLE_ID}
					headingLevel={1}
					className="main-title page-heading"
					MoreInfoContent={<>{getAllTestsTooltipText()}</>}
				/>
				<EvLink
					url={RoutesHelper.getMobileSdkV1DashboardPath()}
					linkText="See older Tests"
					showIcon
					dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_OLDER_TESTS_LINK_ID}
				/>
			</div>
			<EvSection
				ariaLabel="Mobile SDK tests filters"
				ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
				ariaLive="polite"
				skipLinkId="filters-section"
				dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_FILTERS_SECTION_ID}
			>
				{!isLoading ? (
					<div className="filters-area">
						<div className="filter-line">
							<EvFilters
								className="data-filters"
								onApply={handleApply}
								onReset={handleApply}
								initialValues={initialValues}
								filterOptions={filterOptions}
								filterDefinitions={MobileSdkTestsPageFilterDefinitions}
								isFilterOptionsLoaded={!isLoading}
								isInitialValuesLoaded={!isLoading}
							/>
						</div>
						<EvSwitch
							dataAutomationId={MOBILE_SDK_SHOW_ONLY_LATEST_TESTS_TOGGLE_ID}
							onChange={() =>
								onTableParamStatusChange(
									MOBILE_SDK_TESTS_TABLE_ID,
									showRecentTestKey,
									() => !showRecentTest
								)
							}
							checked={showRecentTest}
							label="Show only the last report per test"
							className="show-recent-results-toggle"
						/>
					</div>
				) : (
					<EvSpinner />
				)}
			</EvSection>
		</div>
	);
};
